import { Splide } from '@splidejs/splide';

const sliderInstances = {}; // スライダーインスタンスを保持
const pendingSyncGroups = {}; // 同期待ちのグループ

// 円弧スライダー → いいねボタン（LikeReaction.js）への合図のタイミング。
// スライダーの時間軸（interval / speed）と同じ卓上で調整する値なのでここに置く。
// ※ LIKE_LEAD_MS は _like.scss の --like-out-dur と結合している。
//    LIKE_LEAD_MS >= --like-out-dur + 100ms を割ると、ボタンが消え切る前にカードが動き出す
const LIKE_LEAD_MS      = 400;  // 切り替わりの何ms前から消え始めるか
const LIKE_WATCHDOG_PAD = 400;  // moved が返らなかったとき speed + これ で強制的に再表示する

// 円弧スライダー（js-circleSlider）
// Splide にはいつもどおり横に動いてもらい（内部の位置計算は無傷）、
// カード側でその横移動を打ち消してから遥か下の軸まわりに回す。
// ここで書くのは各スライドの --n（中央からの相対インデックス・小数可）だけで、
// 角度も沈み込みも CSS の transform-origin / rotate が担当する。
// 幾何の定義は _report.scss の &Slider を参照。
//
// 方式は hanshin-ferry.com の Voice スライダーに合わせてある。要点は3つ:
//   ① 動いている間は rAF で「Splide本体の実座標」から毎フレーム描き直す。
//      CSS transition に目的地を渡して補間させない（継ぎ目で15コマぶん補間される事故を根絶）
//   ② 位置がシフトする瞬間のイベント shifted と、慣性の scroll / scrolled まで拾う
//   ③ --n を可視範囲の外で飽和させる。継ぎ目で全長ぶん飛んでも飽和域どうしの移動になり見た目が動かない
const setupCircleSlider = (slider, fallbackLimit) => {
  let baseX = [];
  let unit = 0;
  let raf = null;
  // --n を飽和させるしきい値。正本は _report.scss の --sideFade（= $side + 1）で、
  // 画面幅によって値が変わる。採寸のたびに読み直せば resize にも追従する
  let limit = fallbackLimit;

  const readLimit = () => {
    const value = parseFloat(getComputedStyle(slider.root).getPropertyValue('--sideFade'));
    if (Number.isFinite(value) && value > 0) limit = value;
  };

  // loop のクローンにも --n が要るので Slides.get() ではなく list.children を回す。
  // クローンを取りこぼすと継ぎ目で1枚だけ傾いていないカードが出る
  const getSlides = () => Array.from(slider.Components.Elements.list.children);

  const measure = () => {
    readLimit();
    const slides = getSlides();
    baseX = slides.map(el => el.offsetLeft + el.offsetWidth / 2);
    unit = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
  };

  // 常に Move.getPosition()（= list の実際の translateX）から計算する。
  // 「これから向かう位置」を先読みしないので、Splide が内部で何をしても追従がズレない
  const render = () => {
    const { track } = slider.Components.Elements;
    const pos = slider.Components.Move.getPosition();
    const centerX = track.clientWidth / 2;
    getSlides().forEach((el, i) => {
      const raw = unit ? (baseX[i] + pos - centerX) / unit : 0;
      // 飽和させるのが継ぎ目対策の本体。|--n| が limit を超えた先は
      // どこにいても同じ見た目（opacity 0）なので、ループの巻き戻しが見えない
      const n = raw > limit ? limit : (raw < -limit ? -limit : raw);
      el.style.setProperty('--n', n.toFixed(4));
    });
  };

  // レイアウト確定のタイミング差を吸収するため、1回描くときは二段構えで撃つ
  const renderOnce = () => {
    requestAnimationFrame(render);
    setTimeout(render, 0);
  };

  const startFollowing = () => {
    if (raf !== null) return;
    const tick = () => {
      render();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  };

  const stopFollowing = () => {
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
    renderOnce();
  };

  const remeasure = () => {
    measure();
    renderOnce();
  };

  // 動き出したら追従を始め、止まったら終わる
  slider.on('move drag scroll', startFollowing);
  slider.on('moved dragged scrolled', stopFollowing);
  // shifted = ループの継ぎ目で Splide が位置を全長ぶんシフトした瞬間（Move.translate が emit する）
  slider.on('shifted dragging', renderOnce);
  // クローンが作り直される・寸法が変わる系はベース座標から取り直す
  slider.on('resized updated refresh refreshed', remeasure);

  // タブを戻したときに rAF が止まっていた場合の保険
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) renderOnce();
  });
  window.addEventListener('load', renderOnce);

  // 初回は mount() の直後に呼び出し側から叩く。
  // mounted の中では Splide 内部の Move.reposition がまだ走っておらず
  // getPosition() が 0 を返すため、ここで描くと初期位置が丸ごとずれる
  return { sync: remeasure };
};

const createSlider = (className, defaultOptions) => {
  const targetElms = document.getElementsByClassName(className);
  if (targetElms.length === 0) return;

  // 同期グループがある要素とない要素を分けて処理
  const syncElements = [];
  const normalElements = [];

  for (const targetElm of targetElms) {
    if (targetElm.dataset.syncGroup) {
      syncElements.push(targetElm);
    } else {
      normalElements.push(targetElm);
    }
  }

  // 同期グループの処理
  syncElements.forEach(targetElm => {
    const slideCount = targetElm.querySelectorAll('.splide__slide').length;
    
    const speed = 400;
    let options = {
      type: 'slide',
      speed: speed,
      rewindSpeed: speed,
      ...defaultOptions,
      reduceMotion: {
        speed: speed,
        rewindSpeed: speed,
        autoplay: true,
      },
    };

    if (targetElm.dataset.speed) {
      options.speed = parseInt(targetElm.dataset.speed);
      options.rewindSpeed = parseInt(targetElm.dataset.speed);
      options.reduceMotion.speed = parseInt(targetElm.dataset.speed);
      options.reduceMotion.rewindSpeed = parseInt(targetElm.dataset.speed);
    }

    if (targetElm.dataset.gap) {
      options.gap = targetElm.dataset.gap;
    }

    const slider = new Splide(targetElm, options);

    const syncGroup = targetElm.dataset.syncGroup;
    if (!pendingSyncGroups[syncGroup]) {
      pendingSyncGroups[syncGroup] = {};
    }
    pendingSyncGroups[syncGroup][className] = slider;
  });

  // 通常要素の処理
  normalElements.forEach(targetElm => {
    const slideCount = targetElm.querySelectorAll('.splide__slide').length;
    
    const speed = 400;
    let options = {
      type: 'slide',
      speed: speed,
      rewindSpeed: speed,
      ...defaultOptions,
      reduceMotion: {
        speed: speed,
        rewindSpeed: speed,
        autoplay: true,
      },
    };

    if (targetElm.dataset.speed) {
      options.speed = parseInt(targetElm.dataset.speed);
      options.rewindSpeed = parseInt(targetElm.dataset.speed);
      options.reduceMotion.speed = parseInt(targetElm.dataset.speed);
      options.reduceMotion.rewindSpeed = parseInt(targetElm.dataset.speed);
    }

    if (targetElm.dataset.gap) {
      options.gap = targetElm.dataset.gap;
    }

    if (className === 'js-sliderBasic') {
      if (slideCount < 3) {
        options.drag = false;
        options.arrows = false;
        options.pagination = false;
        options.autoplay = false;
      }
  
      options.breakpoints = {
        ...options.breakpoints,
        991: {
          ...options.breakpoints?.[991],
          drag: slideCount >= 2,
          arrows: slideCount >= 2,
          pagination: slideCount >= 2 && (options.breakpoints?.[991]?.pagination ?? false),
        },
      };
    }

    const slider = new Splide(targetElm, options);

    let circleSlider = null;
    if (className === 'js-circleSlider') {
      // ハンドラは mount 前に張る。初回の描画だけは mount 後（下）に回す。
      // 第2引数は CSS から --sideFade を読めなかったときの保険。
      // 通常は _report.scss の --sideFade（= $side + 1）が使われ、画面幅で切り替わる。
      // ここを超えたカードは opacity 0 なので、飽和させても見た目は変わらない
      circleSlider = setupCircleSlider(slider, 4);

      // いいねボタンへの合図。Splide のイベントを「ボタンのフェーズ」に翻訳して
      // bubbles する DOM イベント1本（slider:phase）だけを公開する。Splide のインスタンスは渡さない。
      // 受け側は LikeReaction.js（節 .p-reportSlider で受けている）。
      //   leave … もうすぐ切り替わる。縮んで消え始める
      //   hide  … 移動開始。即座に非表示
      //   show  … 移動完了。拡大しながら現れる＋小ハート
      //   stay  … 消えかけたが切り替わらなかった。静かに戻す
      let lastPhase = '';     // 同じフェーズの連投を潰す（dragging は毎フレーム飛ぶ）
      let pending   = false;  // hide を投げてから show を投げるまで
      let warned    = false;  // leave の立ち上がりエッジ検出
      let watchdog  = 0;

      const emitPhase = (phase) => {
        lastPhase = phase;
        targetElm.dispatchEvent(new CustomEvent('slider:phase', { bubbles: true, detail: { phase } }));
      };

      // 予告。rate は 0→1 の進捗（壁時計ベース）で、1 に届いた同じフレームで Splide が go('>') する。
      // つまり予告は 1 未満のしきい値でしか作れない。interval を変えても追従するよう毎回引き直す
      slider.on('autoplay:playing', (rate) => {
        const interval  = slider.options.interval || 0;
        const threshold = interval > LIKE_LEAD_MS ? 1 - LIKE_LEAD_MS / interval : 0;
        if (rate < threshold) { warned = false; return; }
        if (warned || pending) return;  // 1周期に1回だけ。移動待ちの間は投げない
        warned = true;
        emitPhase('leave');
      });

      // 移動開始。予告が間に合っていれば見た目は変わらない（もう消えている）
      slider.on('move', () => {
        warned  = false;
        pending = true;
        clearTimeout(watchdog);
        if (lastPhase !== 'hide') emitPhase('hide');
        // 保険。移動中に掴んで離さない／裏タブで transitionend が届かない、のとき moved が返らない。
        // 無いとボタンが消えたまま取り残される
        watchdog = setTimeout(() => {
          if (!pending) return;
          pending = false;
          emitPhase('show');
        }, (slider.options.speed || 0) + LIKE_WATCHDOG_PAD);
      });

      // 移動完了。ウォッチドッグが先に出していたら二重に投げない
      slider.on('moved', () => {
        clearTimeout(watchdog);
        if (!pending) return;
        pending = false;
        emitPhase('show');
      });

      // 指で動かされたときは予告できないので即消し。
      // drag ではなく dragging を見る。drag は1pxのジッタでも飛び、しかもその場合 moved が来ない
      slider.on('dragging', () => {
        if (lastPhase !== 'hide') emitPhase('hide');
      });

      // 消えかけたまま autoplay が止まったら戻す。今の設定では発火経路がほぼ無いが、
      // pauseOnHover / pauseOnFocus を後から有効にしたとき消えたまま固まるのを防ぐ
      slider.on('autoplay:pause', () => {
        if (lastPhase !== 'leave') return;
        warned = false;
        emitPhase('stay');
      });
    }

    if (className === 'js-heroCarousel') {
      let hasLooped = false;
      
      slider.on('moved', (newIndex, prevIndex, destIndex) => {
        if (!hasLooped && newIndex === 0 && prevIndex > 0) {
          hasLooped = true;
          const firstSlide = targetElm.querySelector('.splide__slide:first-child');
          if (firstSlide) {
            firstSlide.classList.add('is-looped');
          }
        }
      });
    }

    slider.mount();

    // mount() が返った時点で Splide の位置決めは終わっている。
    // 同じフレーム内なので、ここで描いてもカードが一瞬ずれて見えることはない
    if (circleSlider) circleSlider.sync();

    if (!sliderInstances[className]) {
      sliderInstances[className] = [];
    }
    sliderInstances[className].push(slider);
  });
};

// 同期グループのスライダーをマウントする関数
const mountSyncGroups = () => {
  Object.keys(pendingSyncGroups).forEach(groupKey => {
    const group = pendingSyncGroups[groupKey];
    
    if (group['js-storyImageCarousel'] && group['js-storyContentCarousel']) {
      const imageSlider = group['js-storyImageCarousel'];
      const contentSlider = group['js-storyContentCarousel'];
      
      contentSlider.sync(imageSlider);
      
      imageSlider.mount();
      contentSlider.mount();

      if (!sliderInstances[groupKey]) {
        sliderInstances[groupKey] = {};
      }
      sliderInstances[groupKey]['js-storyImageCarousel'] = imageSlider;
      sliderInstances[groupKey]['js-storyContentCarousel'] = contentSlider;
    }
  });
};

const init = () => {
  createSlider('js-sliderBasic', {
    perPage: 1,
    perMove: 1,
    gap: '25px',
    arrows: true,
    pagination: false,
    autoWidth: true,
    focus: 0,
    trimSpace: false,
    breakpoints: {
      991: {
        perPage: 1,
        autoWidth: true,
        focus: 'center',
        gap: '10px',
        padding: {left: '5%', right: '5%'},
      },
    },
  });
  
  createSlider('js-heroCarousel', {
    type: 'fade',
    rewind: true,
    autoplay: false,
    interval: 3500,
    speed: 1000,
    arrows: false,
    pagination: false,
  });

  // 円弧スライダー（/report/ SECTION 03）
  createSlider('js-circleSlider', {
    type: 'loop',
    focus: 'center',
    // autoWidth なら箱幅＝CSSで決めたカード幅になる。
    // gap は 0 固定。translateX(-100%) の打ち消しがカード自身の幅を前提にしているため、
    // 余白を足したくなったらカード内側の padding で取ること
    autoWidth: true,
    gap: 0,
    // clones は指定しない。autoWidth のとき Splide は多めにクローンを作る。
    // 弧はトラックより横に広がるので、絞ると継ぎ目で1枚欠ける
    perMove: 1,
    // カードはこの速度・イージングで動く list を rAF で毎フレーム追いかけるだけなので、
    // CSS 側に同じ値を書いて揃える必要はない（ここを変えるだけでよい）。
    // 「切り替わりを気持ちゆっくり」の指摘で 800 → 1100。いいねボタンのウォッチドッグは
    // options.speed を読んでいるので追従する
    speed: 1100,
    rewindSpeed: 1100,
    easing: 'cubic-bezier(0.44, 0.65, 0.07, 1.01)',
    updateOnMove: true,
    arrows: false,
    pagination: false,
    autoplay: true,
    // 1枚あたりの停止時間は interval - speed。3150 なら 2.05秒 見せてから動き出す
    // （「停止時間を .5s 伸ばして」で 4000 → 4500、その後「停止時間を 0.6倍に」で 4500 → 3150）。
    // 速度そのもの（動いている時間）を変えたいときは上の speed を触ること
    interval: 3150,
    // マウスが乗っても止めない（ユーザー指示）。
    // 弧のカードは rAF で list の実座標を追いかけているだけなので、
    // 止まる／動くの切り替わりが起きないぶん追従の乱れも出ない
    pauseOnHover: false,
  });

  createSlider('js-storyImageCarousel', {
    type: 'fade',
    arrows: false,
    pagination: false,
    rewind: true,
  });

  createSlider('js-storyContentCarousel', {
    type: 'fade',
    arrows: true,
    pagination: false,
    rewind: true,
  });

  // 全てのスライダー作成後に同期グループをマウント
  mountSyncGroups();
};

// Splideのautoplayを開始する関数
const startAutoplay = (className) => {
  if (sliderInstances[className]) {
    sliderInstances[className].forEach(slider => {
      slider.Components.Autoplay.play();
    });
  }
};

const startAutoplayWithCustomInterval = (className, firstInterval, normalInterval) => {
  if (sliderInstances[className]) {
    sliderInstances[className].forEach(slider => {
      const autoplay = slider.Components.Autoplay;
      
      // 一度だけ短い間隔で次のスライドへ
      setTimeout(() => {
        slider.go('+1'); // 次のスライドへ移動
        
        // その後、通常のautoplayを開始
        slider.options.interval = normalInterval;
        autoplay.play();
      }, firstInterval);
    });
  }
};

export default init;
export { startAutoplay, startAutoplayWithCustomInterval};
